import React, { useMemo } from 'react';
import { Button, Table, Tag, Tooltip } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { CustomerTier, OrderQuotaDetail, OrderQuotaRow } from '../graphql/orderQuotaGraphql';
import {
    DateColumn,
    OverrideRange,
    fallbackQuota,
    formatQuota,
    overrideOf,
    rowMaxValue,
    unsetMeansUnlimited,
    upcomingOverrideRanges,
} from '../utils/orderQuotaGrid';
import LineList from './LineList';
import QuotaCell from './QuotaCell';

interface OrderQuotaGridProps {
    detail: OrderQuotaDetail;
    rows: OrderQuotaRow[];
    dateColumns: DateColumn[];
    /** 已展开操作入口的行：点击名称展开，再点一次收起，同一时间只展开一行 */
    expandedTargetId?: string;
    onToggleExpand: (targetId: string) => void;
    /** 展开行的「按日批量设置」 */
    onBatch: (row: OrderQuotaRow) => void;
    /** 展开行的「暂停 / 恢复」，只有尾程产能管理传入 */
    onTogglePaused?: (row: OrderQuotaRow) => void;
    onCommitDailyQuota: (row: OrderQuotaRow, value: number) => Promise<void>;
    onCommitOverride: (row: OrderQuotaRow, date: string, value: number | null) => Promise<void>;
}

/** 周末列：浅土黄底，表头周几文字为土黄；今日恰为周末时底色以今日为准 */
const WEEKEND_BG = 'bg-[#fff7e6]';
const WEEKEND_TEXT = 'text-[#d46b08]';

const cellClassName = (column: DateColumn) =>
    [
        column.isToday ? 'bg-blue-50' : column.isWeekend ? WEEKEND_BG : '',
        column.isWeekStart ? 'border-l-2 border-l-gray-300' : '',
    ].join(' ');

/** 浮层最多列出的段数，其余合成一行并写出最远日期 */
const MAX_LISTED_RANGES = 10;

/** 浮层最大宽度（px）：默认 250px 时日期段会折成两行 */
const HINT_MAX_WIDTH = 340;

/**
 * 首列卡片右上角的紫色叹号：该行今日及以后有单独设置的日期时显示，悬停列出日期与值，
 * 避免日期列太多、看不到的日期被误以为都按每天默认值
 */
const UpcomingOverridesHint: React.FC<{ ranges: OverrideRange[] }> = ({ ranges }) => {
    const { t } = useTranslation();
    const lines = ranges.slice(0, MAX_LISTED_RANGES).map(
        (range) =>
            t('central.orderQuota.upcoming.line', {
                dates: range.start === range.end ? range.start : `${range.start} ~ ${range.end}`,
                quota: formatQuota(range.dailyQuota),
            }) as string
    );
    if (ranges.length > MAX_LISTED_RANGES) {
        lines.push(
            t('central.orderQuota.upcoming.more', {
                count: ranges.length - MAX_LISTED_RANGES,
                last: ranges[ranges.length - 1].end,
            }) as string
        );
    }
    const title = t('central.orderQuota.upcoming.title') as string;
    return (
        <Tooltip
            styles={{ root: { maxWidth: HINT_MAX_WIDTH } }}
            title={
                <div>
                    <div>{title}</div>
                    <LineList lines={lines} />
                </div>
            }
        >
            <ExclamationCircleOutlined aria-label={title} className="absolute top-0 right-0 text-[#722ed1] cursor-help" />
        </Tooltip>
    );
};

/**
 * 产能网格：首列管控对象、第二列行日产能（每天默认），其后为日期列；过去日期只读。
 * 分拣产能管理的客户行带分级标签。
 * 不提供行选择：点击名称在其下方展开该行的操作入口，避免与工具栏「导入 Excel」（导入全部）混淆。
 */
const OrderQuotaGrid: React.FC<OrderQuotaGridProps> = ({
    detail,
    rows,
    dateColumns,
    expandedTargetId,
    onToggleExpand,
    onBatch,
    onTogglePaused,
    onCommitDailyQuota,
    onCommitOverride,
}) => {
    const { t } = useTranslation();
    const unsetUnlimited = unsetMeansUnlimited(detail.strategyType);

    const columns: ColumnsType<OrderQuotaRow> = useMemo(
        () => [
            {
                title: t(`central.orderQuota.target.${detail.strategyType}`),
                key: 'target',
                fixed: 'left',
                width: 220,
                onCell: (row) => ({
                    className: row.targetId === expandedTargetId ? 'shadow-[inset_3px_0_0_#722ed1]' : '',
                }),
                render: (_, row) => {
                    const expanded = row.targetId === expandedTargetId;
                    const upcoming = upcomingOverrideRanges(row, detail.today);
                    return (
                        <div className={`relative flex flex-col items-start ${upcoming.length > 0 ? 'pr-5' : ''}`}>
                            {upcoming.length > 0 && <UpcomingOverridesHint ranges={upcoming} />}
                            <button
                                type="button"
                                aria-expanded={expanded}
                                className="p-0 border-0 bg-transparent text-left font-medium cursor-pointer hover:text-[#722ed1]"
                                onClick={() => onToggleExpand(row.targetId)}
                            >
                                {row.customerTier !== null && (
                                    <Tag color={row.customerTier === CustomerTier.HIGH ? 'green' : 'orange'} className="mr-2">
                                        {t(`central.orderQuota.tier.${row.customerTier}`)}
                                    </Tag>
                                )}
                                {row.targetName}
                                {row.isPaused && (
                                    <Tag color="orange" className="ml-2">
                                        {t('central.orderQuota.paused')}
                                    </Tag>
                                )}
                            </button>
                            <span className="text-xs text-gray-400">{row.targetCode}</span>
                            {expanded && (
                                <div className="flex items-center gap-3 mt-1">
                                    <Button type="link" size="small" className="p-0 h-auto" onClick={() => onBatch(row)}>
                                        {t('central.orderQuota.actions.batch')}
                                    </Button>
                                    {onTogglePaused && (
                                        <Button type="link" size="small" className="p-0 h-auto" onClick={() => onTogglePaused(row)}>
                                            {row.isPaused ? t('central.orderQuota.actions.resume') : t('central.orderQuota.actions.pause')}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                title: t(`central.orderQuota.rowDailyQuota.${detail.strategyType}`),
                key: 'dailyQuota',
                fixed: 'left',
                width: 130,
                render: (_, row) => (
                    <QuotaCell
                        value={row.dailyQuota}
                        fallback={row.dailyQuota === null ? fallbackQuota(row) : undefined}
                        allowEmpty={false}
                        maxValue={rowMaxValue(row, detail)}
                        unsetUnlimited={unsetUnlimited}
                        onCommit={(value) => onCommitDailyQuota(row, value ?? 0)}
                    />
                ),
            },
            ...dateColumns.map((column) => ({
                title: (
                    <div className="flex flex-col items-center leading-tight">
                        <span>{dayjs(column.date).format('MM-DD')}</span>
                        <span className={`text-xs ${column.isWeekend ? WEEKEND_TEXT : 'text-gray-400'}`}>
                            {t(`central.orderQuota.weekday.${dayjs(column.date).day()}`)}
                        </span>
                    </div>
                ),
                key: column.date,
                width: 96,
                onHeaderCell: () => ({ className: cellClassName(column) }),
                onCell: () => ({ className: cellClassName(column) }),
                render: (_: unknown, row: OrderQuotaRow) => (
                    <QuotaCell
                        value={overrideOf(row, column.date)}
                        fallback={fallbackQuota(row)}
                        readOnly={column.isPast}
                        allowEmpty
                        maxValue={rowMaxValue(row, detail)}
                        unsetUnlimited={unsetUnlimited}
                        onCommit={(value) => onCommitOverride(row, column.date, value)}
                    />
                ),
            })),
        ],
        [t, detail, dateColumns, unsetUnlimited, expandedTargetId, onToggleExpand, onBatch, onTogglePaused, onCommitDailyQuota, onCommitOverride]
    );

    return (
        <Table<OrderQuotaRow>
            rowKey="targetId"
            size="small"
            bordered
            pagination={false}
            columns={columns}
            dataSource={rows}
            scroll={{ x: 350 + dateColumns.length * 96 }}
        />
    );
};

export default OrderQuotaGrid;
